import { Input } from '@/components/atoms';
import { InputErrorMessage as Error } from '@/components';
import { OTPFields } from '@/components/organisms/Authentication/OTPForm';
import { get } from 'lodash';
import { DIGIT_RULE, ONE_NUMBER_RULE } from '@/utils/constants';
import { FC, useMemo } from 'react';
import { FieldErrors, Path } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { AnimatePresence } from 'framer-motion';
import APP_CONFIG from '@/config/app';

const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;

type Props = {
  value: string;
  valueLength: number;
  onChange: (value: string) => void;
  name: Path<OTPFields>;
  errors: FieldErrors<OTPFields>;
  clearErrors?: (name?: Path<OTPFields>) => void;
  showError?: boolean;
};

const OTPInput: FC<Props> = ({
  value,
  valueLength,
  onChange,
  name,
  errors,
  clearErrors,
  showError,
}) => {
  const valueDigits = useMemo(() => {
    const digits = value.split('');
    const items: Array<string> = [];

    for (let i = 0; i < valueLength; i++) {
      const digit = digits[i];

      if (DIGIT_RULE.test(digit)) items.push(digit);
      else items.push('');
    }

    return items;
  }, [value, valueLength]);

  const getPreviousInput = (target: HTMLElement) =>
    target.parentElement?.parentElement?.previousElementSibling?.querySelector(
      'input',
    );

  const getNextInput = (target: HTMLElement) =>
    target.parentElement?.parentElement?.nextElementSibling?.querySelector(
      'input',
    );

  const focusToNextInput = (target: HTMLElement) => {
    const nextInput = getNextInput(target);
    nextInput?.focus();
  };

  const focusToPreviousInput = (target: HTMLElement) => {
    const previousInput = getPreviousInput(target);
    previousInput?.focus();
  };

  const handleDigitChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const target = e.target;
    let targetValue = target.value;
    const isTargetValueValid = DIGIT_RULE.test(targetValue);

    if (!isTargetValueValid && targetValue !== '') return;

    const nextInput = getNextInput(target);
    if (!isTargetValueValid && nextInput && nextInput.value !== '') return;

    targetValue = isTargetValueValid ? targetValue : ' ';

    if (targetValue.length === 1) {
      const newValue =
        value.substring(0, index) +
        targetValue.trim() +
        value.substring(index + 1);

      onChange(newValue);

      if (!isTargetValueValid) {
        return;
      }

      focusToNextInput(target);
    } else if (targetValue.length === valueLength) {
      onChange(targetValue);
      target.blur();
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const target = e.currentTarget;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault();
      return focusToNextInput(target);
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault();
      return focusToPreviousInput(target);
    }

    const targetValue = target.value;

    target.setSelectionRange(0, targetValue.length);

    if (key !== 'Backspace' || targetValue !== '') return;

    focusToPreviousInput(target);
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { target } = e;

    const previousInput = getPreviousInput(target);

    if (previousInput && previousInput.value === '') {
      return previousInput.focus();
    }

    target.setSelectionRange(0, target.value.length);
  };

  const errorMessage = get(errors, name);
  const hasError = !!(errors && errorMessage);

  const handleOnBlur = () => {
    if (hasError) {
      clearErrors && clearErrors(name);
    }
  };

  return (
    <div>
      <div
        className={
          hasError ? 'bg-error/15 flex gap-4 rounded-t-md' : 'flex gap-4'
        }
      >
        {valueDigits.map((digit, index) => {
          return (
            <Input
              key={index}
              size="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern={ONE_NUMBER_RULE.source}
              maxLength={valueLength}
              value={digit}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleDigitChange(e, index)
              }
              onKeyDown={handleOnKeyDown}
              onFocus={handleOnFocus}
              intent={hasError ? 'digitError' : undefined}
              aria-invalid={hasError}
              showError={showError}
              onBlur={handleOnBlur}
              className="pl-0"
            />
          );
        })}
      </div>
      <ErrorMessage
        errors={errors}
        name={name as any}
        render={({ message }) =>
          message && showError ? (
            <AnimatePresence>
              <Error
                message={message}
                className={`relative bottom-0.5 left-0 right-0 backdrop-blur${MAIN_BACKDROP_BLUR}`}
              />
            </AnimatePresence>
          ) : null
        }
      />
    </div>
  );
};

export default OTPInput;
